import { useNavigate } from "@tanstack/react-router";
import type { Uuid } from "spacetimedb";

import { ActionCard } from "@/web/routes/_protected/smb/-dashboard/-ActionCard";
import type {
  SmbActionCoreProps,
  SmbActionDefinition,
} from "@/web/routes/_protected/smb/-dashboard/-types";

type ComposeTtwProps = {
  /** The caller's underwritable entity. Threaded through for id-scoping and
   * future param support; the composer route resolves the caller's card
   * itself today (navigation is param-less). */
  underwritableEntity: Uuid;
  /**
   * Whether a composer (TTW) page already exists for the brand's card. Flips
   * the card between its "Compose" (first draft) and "Edit" (update in place)
   * copy. Sourced from `CardEngineInput.shareableBusinessCards[].hasComposerPage`.
   */
  hasComposerPage: boolean;
};

function ComposeTtwAction({ hasComposerPage, onSkip }: ComposeTtwProps & SmbActionCoreProps) {
  const navigate = useNavigate();

  return (
    <ActionCard category="fundraise">
      <ActionCard.Header>
        <ActionCard.Badge>Fundraise</ActionCard.Badge>
        <ActionCard.DetailsTrigger />
      </ActionCard.Header>
      <ActionCard.Main>
        <ActionCard.Title>
          {hasComposerPage ? "Update your investor page" : "Design your investor page"}
        </ActionCard.Title>
        <ActionCard.Description>
          {hasComposerPage
            ? "Keep your public investor page fresh — refine the story, swap in new photos or video, and adjust your ask."
            : "Build a public page that tells your story to potential investors — chat with an assistant to design it, then share the link."}
        </ActionCard.Description>
      </ActionCard.Main>
      <ActionCard.Footer>
        <ActionCard.SkipButton onSkip={onSkip} />
        <ActionCard.PrimaryButton onClick={() => navigate({ to: "/profile/compose" })}>
          {hasComposerPage ? "Edit page" : "Compose"}
        </ActionCard.PrimaryButton>
      </ActionCard.Footer>
      <ActionCard.Back title="Why we do this">
        <p>
          Your investor page is what people see when you share your business — a polished, on-brand
          pitch for a "testing-the-waters" raise. Composing it here keeps it tied to your shareable
          card, so the same link always shows your latest page.
        </p>
      </ActionCard.Back>
    </ActionCard>
  );
}

export function createComposeTtwAction(
  props: ComposeTtwProps & { id?: string; canSkip?: boolean },
): SmbActionDefinition {
  const { id = "compose-ttw", canSkip = true, ...rest } = props;
  return {
    id,
    category: "fundraise",
    canSkip,
    render: (core) => <ComposeTtwAction {...rest} {...core} />,
  };
}

// ── Engine rules ─────────────────────────────────────────────────────────────
import { CardRulesEngine } from "@/web/routes/_protected/smb/-dashboard/-CardRulesEngine";
import { createComposeTtwCompleted } from "@/web/routes/_protected/smb/-dashboard/-completedActions/ComposeTtwCompleted";

// Semantic gate: as soon as the brand has a shareable business card, invite the
// user to compose (or edit) their investor page. Stays permanently in the
// active deck for as long as a card exists — Skip only dismisses it for the
// session, mirroring the Share card. The button copy flips to "Edit page" once
// a page has been saved.
export const composeTtwRule = CardRulesEngine.singletonRule({
  id: "compose-ttw",
  priority: 20,
  when: CardRulesEngine.hasShareableBusinessCard,
  build: (d) =>
    createComposeTtwAction({
      underwritableEntity: d.underwritableEntityId,
      hasComposerPage: CardRulesEngine.hasComposerPage(d),
    }),
});

// One completed card per card that has a saved page. Iterating the cards (vs. a
// singleton gate) keeps each card row — and thus `card.id` for the view link —
// defined, and naturally handles a multi-card brand.
export const composeTtwCompletedRule = CardRulesEngine.forEachCompletedRule({
  id: "compose-ttw:done",
  forEach: (d) => d.shareableBusinessCards.filter((c) => c.hasComposerPage),
  when: CardRulesEngine.always,
  build: (_, card) => createComposeTtwCompleted({ cardId: card.id }),
});
